import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import type { TagDefinition } from '../../../../../shared/tags/tagRegistry'
import { type SearchResult, apiService } from '../../../services/api'
import { logger } from '../../../utils/logger'
import { FLAGGED_SPOTS_JOURNAL_FILENAME, FLAGGED_SPOTS_TAG_NAME, type FlaggedSpotItem } from '../flaggedSpots'

interface UseFlaggedSpotsPromptArgs {
  /** True in author/writer mode (the /edit route), false for readers. */
  enabled: boolean
  bookId?: string
  /** Current route chapter param (a source filename), if any. */
  chapterId?: string
}

interface UseFlaggedSpotsPromptResult {
  open: boolean
  /** The tag's display name, shown as the dialog title. */
  title: string
  items: FlaggedSpotItem[]
  onPick: (item: FlaggedSpotItem) => void
  onClose: () => void
  onGoToJournal: () => void
}

function resolveFlaggedTagIds(tags: Record<string, TagDefinition>): string[] {
  return Object.entries(tags)
    .filter(([, def]) => def?.name?.trim() === FLAGGED_SPOTS_TAG_NAME)
    .map(([id]) => id)
}

function mapResults(results: SearchResult[]): FlaggedSpotItem[] {
  const seen = new Set<string>()
  const items: FlaggedSpotItem[] = []
  for (const result of results) {
    const blockId = result.annotationType === 'file' ? null : result.blockId || null
    const dedupeKey = `${result.filename}::${blockId ?? ''}`
    if (seen.has(dedupeKey)) {
      continue
    }
    seen.add(dedupeKey)
    const path = result.headingPath ?? []
    const title = path.length > 0 ? path[path.length - 1]! : result.chapterTitle || result.filename
    items.push({
      filename: result.filename,
      blockId,
      title,
      snippet: (result.snippet ?? '').trim(),
      context: path.slice(0, -1),
    })
  }
  return items
}

/**
 * Writer-launch reminder: on every full editor load in writer mode (launch, reload, a
 * directly opened URL — fired once per mount, not on in-app navigation), surface every
 * spot carrying the flag tag and let the author jump straight to one.
 *
 * The lookup fires immediately on mount and runs in parallel with the editor's own load
 * (it fetches the tag registry itself rather than waiting on the editor's heavier tag
 * data) so the dialog appears as early as possible. It opens only once results are in, so
 * nothing flashes when nothing is flagged. Dismissing leaves the author on whatever the
 * editor already loaded behind the dialog.
 */
export function useFlaggedSpotsPrompt({
  enabled,
  bookId,
  chapterId,
}: UseFlaggedSpotsPromptArgs): UseFlaggedSpotsPromptResult {
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const [items, setItems] = useState<FlaggedSpotItem[]>([])
  const firedRef = useRef(false)
  const resolvedRef = useRef(false)
  const mountedRef = useRef(true)

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  const onPick = useCallback(
    (item: FlaggedSpotItem) => {
      if (resolvedRef.current || !bookId) {
        return
      }
      resolvedRef.current = true
      setOpen(false)
      const target = item.blockId
        ? `/edit/${bookId}/${item.filename}#${item.blockId}`
        : `/edit/${bookId}/${item.filename}`
      navigate(target)
    },
    [bookId, navigate],
  )

  // Dismiss leaves the author on whatever is already loaded behind the dialog.
  const onClose = useCallback(() => {
    resolvedRef.current = true
    setOpen(false)
  }, [])

  const onGoToJournal = useCallback(() => {
    if (resolvedRef.current || !bookId) {
      return
    }
    resolvedRef.current = true
    setOpen(false)
    if (chapterId !== FLAGGED_SPOTS_JOURNAL_FILENAME) {
      navigate(`/edit/${bookId}/${FLAGGED_SPOTS_JOURNAL_FILENAME}`)
    }
  }, [bookId, chapterId, navigate])

  useEffect(() => {
    if (firedRef.current || !enabled || !bookId) {
      return
    }
    firedRef.current = true

    const runPrompt = async () => {
      let mapped: FlaggedSpotItem[] = []
      try {
        const registry = await apiService.getTagRegistry(bookId)
        const tagIds = resolveFlaggedTagIds(registry.data?.tags ?? {})
        if (tagIds.length > 0) {
          const response = await apiService.searchChapters(bookId, undefined, tagIds)
          mapped = response.success ? mapResults(response.data) : []
        }
      } catch (error) {
        logger.error('Flagged-spots prompt failed:', error)
      }

      if (!mountedRef.current || mapped.length === 0) {
        return
      }
      setItems(mapped)
      setOpen(true)
    }

    void runPrompt()
  }, [enabled, bookId])

  return { open, title: FLAGGED_SPOTS_TAG_NAME, items, onPick, onClose, onGoToJournal }
}
